// Custom horizontal roadmap for the Construction page.
// Linear / Height / Notion-style aesthetic.
//
//   - Sticky month scale across the top
//   - Phases as group rows, tasks as indented child rows beneath
//   - Phases are collapsible
//   - Bars: track + gradient progress fill
//   - Critical-path: red border + red dot in label
//   - Milestones: diamond shape
//   - Today: thin red vertical line + "TODAY" tag
//   - Color per phase_type
//
// View window: caller controls via viewStart/viewEnd props. If unset, derives
// from min/max of items. When window span is large, supports horizontal scroll
// with sticky label column.

import { useMemo, useState } from 'react';

export type RoadmapItem = {
  id: string;
  parent_id?: string | null;
  template_code?: string | null;
  name: string;
  start: Date;
  end: Date;
  progress: number;
  status: string;
  is_milestone?: boolean;
  is_critical_path?: boolean;
  phase_type?: string;
  kind: 'phase' | 'task';
};

const PHASE_COLOR: Record<string, { fill: string; track: string; soft: string }> = {
  preconstruction: { fill: '#3b82f6', track: '#dbeafe', soft: '#eff6ff' },
  procurement:     { fill: '#f59e0b', track: '#fef3c7', soft: '#fffbeb' },
  construction:    { fill: '#10b981', track: '#d1fae5', soft: '#ecfdf5' },
  commissioning:   { fill: '#8b5cf6', track: '#ede9fe', soft: '#f5f3ff' },
  launch:          { fill: '#ec4899', track: '#fce7f3', soft: '#fdf2f8' },
  ongoing:         { fill: '#6b7280', track: '#e5e7eb', soft: '#f3f4f6' },
};

const MS_DAY = 86_400_000;

function monthsBetween(start: Date, end: Date): Date[] {
  const out: Date[] = [];
  const c = new Date(start.getFullYear(), start.getMonth(), 1);
  const e = new Date(end.getFullYear(), end.getMonth(), 1);
  while (c <= e) {
    out.push(new Date(c));
    c.setMonth(c.getMonth() + 1);
  }
  return out;
}

function pctBetween(d: Date, viewStart: Date, viewEnd: Date): number {
  const total = viewEnd.getTime() - viewStart.getTime();
  return total > 0 ? ((d.getTime() - viewStart.getTime()) / total) * 100 : 0;
}

function fmtRange(s: Date, e: Date): string {
  const f = (d: Date) => d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  return `${f(s)} – ${f(e)}`;
}

interface Props {
  items: RoadmapItem[];
  viewStart?: Date;
  viewEnd?: Date;
  today?: Date;
  onItemClick?: (item: RoadmapItem) => void;
  defaultCollapsedPhases?: string[];
  /**
   * Minimum pixel width per month. If months × minMonthWidth + label exceeds
   * available width, the timeline becomes horizontally scrollable with the
   * label column sticky-pinned to the left edge. Default 60.
   */
  minMonthWidth?: number;
}

const LABEL_W = 260;
const ROW_H_PHASE = 42;
const ROW_H_TASK = 32;

export default function PhaseRoadmap({
  items,
  viewStart: propStart,
  viewEnd: propEnd,
  today = new Date(),
  onItemClick,
  defaultCollapsedPhases = [],
  minMonthWidth = 60,
}: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set(defaultCollapsedPhases));
  const [hover, setHover] = useState<string | null>(null);

  // View bounds: explicit props win, else derive from items.
  const { viewStart, viewEnd, months } = useMemo(() => {
    let start = propStart ? new Date(propStart) : null;
    let end   = propEnd   ? new Date(propEnd)   : null;

    if (!start || !end) {
      if (items.length === 0) {
        return { viewStart: today, viewEnd: today, months: [] as Date[] };
      }
      let min = items[0].start.getTime();
      let max = items[0].end.getTime();
      for (const i of items) {
        if (i.start.getTime() < min) min = i.start.getTime();
        if (i.end.getTime() > max) max = i.end.getTime();
      }
      start = new Date(min - 3 * MS_DAY);
      end   = new Date(max + 3 * MS_DAY);
    }
    // Snap start to month-1, end to last day of its month.
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 0);
    return { viewStart: start, viewEnd: end, months: monthsBetween(start, end) };
  }, [items, today, propStart, propEnd]);

  // Filter to items whose range intersects the view window.
  const inWindow = useMemo(() => {
    return items.filter(i =>
      i.end.getTime() >= viewStart.getTime() && i.start.getTime() <= viewEnd.getTime()
    );
  }, [items, viewStart, viewEnd]);

  // Visible = window-filtered AND collapse-filtered.
  const visible = useMemo(() => {
    return inWindow.filter(i => {
      if (i.kind === 'phase') return true;
      if (i.parent_id && collapsed.has(i.parent_id)) return false;
      return true;
    });
  }, [inWindow, collapsed]);

  const toggleCollapse = (phaseId: string) => {
    setCollapsed(s => {
      const next = new Set(s);
      if (next.has(phaseId)) next.delete(phaseId);
      else next.add(phaseId);
      return next;
    });
  };
  const expandAll   = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(items.filter(i => i.kind === 'phase').map(i => i.id)));

  const todayPct = pctBetween(today, viewStart, viewEnd);
  const todayInView = todayPct >= 0 && todayPct <= 100;

  if (items.length === 0) {
    return (
      <div className="card">
        <div className="card__body" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
          No phases scheduled.
        </div>
      </div>
    );
  }

  // Decide whether timeline should scroll horizontally.
  const totalMonths = months.length;
  const minTimelineWidth = totalMonths * minMonthWidth;
  // We don't measure container width; we compare against a typical 1000px timeline area.
  // If desired min is bigger, force scroll.
  const TYPICAL_TIMELINE_W = 1000;
  const scrolls = minTimelineWidth > TYPICAL_TIMELINE_W;

  // Computed timeline width when scrolling.
  const timelineW = scrolls ? minTimelineWidth : null;

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 16px', borderBottom: '1px solid #e2e8f0',
        background: 'white', fontSize: 12, color: '#64748b',
      }}>
        <div>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{visible.filter(i => i.kind === 'phase').length}</span>
          <span> phases · </span>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{visible.filter(i => i.kind === 'task').length}</span>
          <span> tasks · in view </span>
          <span>{viewStart.toLocaleString('en-US', { month: 'short', year: 'numeric' })} – {viewEnd.toLocaleString('en-US', { month: 'short', year: 'numeric' })}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={expandAll}
            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 3, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>
            Expand all
          </button>
          <button onClick={collapseAll}
            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 3, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>
            Collapse all
          </button>
        </div>
      </div>

      {/* Outer scroll container */}
      <div style={{ overflowX: scrolls ? 'auto' : 'hidden', overflowY: 'auto', maxHeight: 580 }}>
        {/* Inner: fixed-min-width when scrolling */}
        <div style={{ minWidth: scrolls ? timelineW! + LABEL_W : '100%', position: 'relative' }}>

          {/* Month scale (sticky-top) */}
          <div style={{
            display: 'flex',
            position: 'sticky', top: 0, zIndex: 3,
            background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
            height: 38,
          }}>
            <div style={{
              width: LABEL_W, flexShrink: 0,
              position: 'sticky', left: 0, zIndex: 4,
              padding: '0 16px', display: 'flex', alignItems: 'center',
              fontSize: 10, fontWeight: 700, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: '.06em',
              background: '#f8fafc',
              borderRight: '1px solid #e2e8f0',
            }}>
              Phase / Task
            </div>
            <div style={{ flex: 1, position: 'relative', minWidth: scrolls ? timelineW! : 'auto' }}>
              {months.map((m, i) => {
                const left  = pctBetween(m, viewStart, viewEnd);
                const next  = months[i + 1] ?? viewEnd;
                const right = pctBetween(next, viewStart, viewEnd);
                const w = right - left;
                const isJan = m.getMonth() === 0;
                return (
                  <div key={m.toISOString()} style={{
                    position: 'absolute',
                    left: left + '%', width: w + '%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 600,
                    color: isJan ? '#0f172a' : '#64748b',
                    borderRight: '1px solid #e2e8f0',
                    textTransform: 'uppercase', letterSpacing: '.04em',
                    overflow: 'hidden', whiteSpace: 'nowrap',
                  }}>
                    {m.toLocaleString('en-US', { month: 'short' })}
                    {isJan && <span style={{ marginLeft: 4, color: '#94a3b8', fontSize: 9 }}>'{String(m.getFullYear()).slice(-2)}</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Today line — overlay across all rows in body area */}
          {todayInView && (
            <>
              <div style={{
                position: 'absolute',
                left: `calc(${LABEL_W}px + (100% - ${LABEL_W}px) * ${todayPct / 100})`,
                top: 38, bottom: 0, width: 1,
                background: '#ef4444', zIndex: 2, pointerEvents: 'none',
                boxShadow: '0 0 0 1px rgba(239,68,68,0.15)',
              }} />
              <div style={{
                position: 'absolute',
                left: `calc(${LABEL_W}px + (100% - ${LABEL_W}px) * ${todayPct / 100} + 4px)`,
                top: 42,
                fontSize: 9, fontWeight: 700, color: '#dc2626',
                textTransform: 'uppercase', letterSpacing: '.06em',
                pointerEvents: 'none', zIndex: 2,
              }}>Today</div>
            </>
          )}

          {/* Rows */}
          {visible.map(item => {
            const colors = PHASE_COLOR[item.phase_type ?? 'preconstruction'] ?? PHASE_COLOR.preconstruction;
            const left  = pctBetween(item.start, viewStart, viewEnd);
            const right = pctBetween(item.end,   viewStart, viewEnd);
            const width = Math.max(right - left, 0.4);
            const isPhase = item.kind === 'phase';
            const rowH = isPhase ? ROW_H_PHASE : ROW_H_TASK;
            const hasChildren = isPhase && items.some(i => i.parent_id === item.id);
            const isCollapsed = collapsed.has(item.id);
            const isHover = hover === item.id;

            const labelBg = isPhase
              ? (isHover ? colors.soft : '#fafbfc')
              : (isHover ? '#f8fafc' : 'white');

            return (
              <div
                key={item.id}
                onMouseEnter={() => setHover(item.id)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onItemClick?.(item)}
                style={{
                  display: 'flex',
                  height: rowH,
                  borderBottom: '1px solid #f1f5f9',
                  background: labelBg,
                  cursor: 'pointer',
                  transition: 'background 0.12s',
                }}>
                {/* Sticky-left label column */}
                <div style={{
                  width: LABEL_W, flexShrink: 0,
                  position: 'sticky', left: 0, zIndex: 1,
                  padding: isPhase ? '0 16px' : `0 16px 0 ${item.parent_id ? 36 : 16}px`,
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: isPhase ? 13 : 12,
                  fontWeight: isPhase ? 700 : 500,
                  color: isPhase ? '#0f172a' : '#475569',
                  borderRight: '1px solid #e2e8f0',
                  background: labelBg,
                  overflow: 'hidden',
                }}>
                  {isPhase && (
                    <div style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
                      background: colors.fill,
                    }}/>
                  )}
                  {hasChildren && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleCollapse(item.id); }}
                      style={{
                        width: 16, height: 16, padding: 0, marginLeft: -4,
                        background: 'transparent', border: 0, cursor: 'pointer',
                        color: '#94a3b8', fontSize: 9, lineHeight: 1, flexShrink: 0,
                      }}>
                      {isCollapsed ? '▶' : '▼'}
                    </button>
                  )}
                  {item.is_critical_path && (
                    <span title="On critical path"
                      style={{ width: 6, height: 6, borderRadius: 999, background: '#ef4444', flexShrink: 0 }} />
                  )}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.template_code && (
                      <span style={{
                        color: '#94a3b8', marginRight: 6,
                        fontVariantNumeric: 'tabular-nums',
                      }}>{item.template_code}</span>
                    )}
                    {item.name}
                  </span>
                </div>

                {/* Timeline area */}
                <div style={{ flex: 1, position: 'relative', minWidth: scrolls ? timelineW! : 'auto' }}>
                  {item.is_milestone ? (
                    <div title={`${item.name} — ${fmtRange(item.start, item.end)}`}
                      style={{
                        position: 'absolute',
                        left: `calc(${left}% - 8px)`,
                        top: rowH / 2 - 8,
                        width: 16, height: 16,
                        background: item.status === 'complete' ? colors.fill : 'white',
                        border: `2px solid ${colors.fill}`,
                        transform: 'rotate(45deg)',
                        boxShadow: '0 1px 3px rgba(15,23,42,0.18)',
                        transition: 'left 0.45s cubic-bezier(0.4, 0.0, 0.2, 1)',
                      }}/>
                  ) : (
                    <>
                      <div title={`${item.name} — ${fmtRange(item.start, item.end)} (${item.progress}%)`}
                        style={{
                          position: 'absolute',
                          left: left + '%',
                          width: width + '%',
                          top: isPhase ? 9 : 7,
                          height: isPhase ? 24 : 18,
                          background: colors.track,
                          borderRadius: 4,
                          border: item.is_critical_path ? '1.5px solid #dc2626' : `1px solid ${colors.track}`,
                          boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                          overflow: 'hidden',
                          transition: 'left 0.45s cubic-bezier(0.4, 0.0, 0.2, 1), width 0.45s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        }}>
                        <div style={{
                          position: 'absolute', left: 0, top: 0, bottom: 0,
                          width: Math.max(0, Math.min(100, item.progress)) + '%',
                          background: `linear-gradient(180deg, ${colors.fill} 0%, ${colors.fill}DD 100%)`,
                          transition: 'width 0.3s ease',
                        }}/>
                      </div>
                      {/* Floating label — clamped into the visible area so it stays
                          legible when the bar starts before viewStart. */}
                      {isPhase && item.progress > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `clamp(0%, ${left}%, calc(100% - 44px))`,
                            top: rowH / 2 - 8,
                            padding: '1px 6px',
                            fontSize: 10, fontWeight: 700,
                            color: 'white',
                            whiteSpace: 'nowrap',
                            textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                            pointerEvents: 'none',
                            transition: 'left 0.45s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          }}>
                          {item.progress}%
                        </div>
                      )}
                      {/* Off-screen continuation indicators */}
                      {left < 0 && (
                        <div style={{
                          position: 'absolute', left: 2, top: rowH / 2 - 7,
                          fontSize: 12, color: colors.fill, fontWeight: 700,
                          pointerEvents: 'none',
                        }}>‹</div>
                      )}
                      {right > 100 && (
                        <div style={{
                          position: 'absolute', right: 2, top: rowH / 2 - 7,
                          fontSize: 12, color: colors.fill, fontWeight: 700,
                          pointerEvents: 'none',
                        }}>›</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
